import {
  composeSDKFactories,
  createElementPropsSDKFactory,
  resolveAndFetchSvg,
  toJSONBase,
  withValidation,
  reportWarning,
  createAccessibilityPropSDKFactory,
} from '@wix/editor-elements-corvid-utils';

import type {
  CorvidSDKApi,
  IPlatformHandlers,
} from '@wix/editor-elements-types/corvid';
import { createComponentSDKModel } from '@wix/editor-elements-integrations/corvid';
import {
  getBreadcrumbsItems,
  canRenderEllipsis,
  getPageHierarchyBreadcrumbs,
} from '../Breadcrumbs.utils';
import type {
  BreadcrumbsMapperProps,
  Breadcrumb,
  BreadcrumbMetaData,
  BreadcrumbsProps,
  IBreadcrumbsSDK,
  IBreadcrumbsSDKFactory,
  IAccessibilitySDKFactory,
} from '../Breadcrumbs.types';
import {
  customValidators,
  validationSchema,
  validateLimits,
} from './Breadcrumbs.validations';

export const ELLIPSIS_WARNING_MESSAGE: string =
  'The number of items to show before or after the ellipsis exceeds the number of defined breadcrumb items. All breadcrumb items are shown.';

const resolveIcons = (
  breadcrumbs: Array<BreadcrumbMetaData>,
  mediaSvgUrl: string,
  sanitizeSVG: IPlatformHandlers['sanitizeSVG'],
) => {
  return Promise.all(
    breadcrumbs.map(async breadcrumb => {
      if (!breadcrumb.icon) {
        return breadcrumb;
      }

      const icon = await resolveAndFetchSvg(
        breadcrumb.icon,
        mediaSvgUrl,
        sanitizeSVG,
      );

      return {
        ...breadcrumb,
        icon,
      };
    }),
  );
};

const resolveLinks = (breadcrumbs: Array<BreadcrumbMetaData>) => {
  return breadcrumbs.map(breadcrumb => {
    if (breadcrumb.link) {
      return {
        ...breadcrumb,
        link: breadcrumb.link.href,
      };
    }

    return breadcrumb;
  }) as Array<Breadcrumb>;
};

const makeInitialBreadcrumbs = (
  envData: CorvidSDKApi['envData'],
  platformUtils: CorvidSDKApi['platformUtils'],
  props: BreadcrumbsMapperProps,
) => {
  const {
    pagesMenuItems,
    isMobile,
    hasPageHierarchyBreadcrumbs,
    pagesMap,
    mainPageId,
  } = props;
  const menuData = { pagesMenuItems, isMobile: Boolean(isMobile) };

  const { linkUtils, wixCodeNamespacesRegistry } = platformUtils;
  const { relativeUrl, externalBaseUrl, pageId } = envData.router.routingInfo;
  const { currentPage } = wixCodeNamespacesRegistry.get('site');

  const routeInfo = {
    homePageHref: externalBaseUrl,
    currentUrl: linkUtils.getLinkProps(relativeUrl.slice(1)).href!,
    pageTitle: currentPage.name,
    pageId,
  };

  return hasPageHierarchyBreadcrumbs
    ? getPageHierarchyBreadcrumbs(pagesMap, routeInfo, mainPageId, props)
    : getBreadcrumbsItems(routeInfo, menuData, props);
};

const breadcrumbsSDKFactory: IBreadcrumbsSDKFactory = ({
  setProps,
  props,
  metaData,
  createSdkState,
  sdkData,
  platformUtils,
  envData,
  handlers,
}) => {
  const { linkUtils } = platformUtils;

  const makeBreadcrumbsProps = async (
    breadcrumbs: Array<BreadcrumbMetaData>,
  ) => {
    const withIcons = await resolveIcons(
      breadcrumbs,
      sdkData.mediaSvgUrl,
      handlers.sanitizeSVG,
    );
    const result = resolveLinks(withIcons);

    return { breadcrumbs: result };
  };

  const makeBreadcrumbsMeta = (breadcrumbs: Array<Breadcrumb>) => {
    return breadcrumbs.map(item => {
      const result: BreadcrumbMetaData = {};

      if (item.label) {
        result.label = item.label;
      }

      if (item.icon) {
        result.icon = item.icon;
      }

      if (item.link) {
        /**
         * By default, the link to the home page received from platform is absolute
         * and will be converted to ExternalLink by getLinkProps.
         *
         * Which will lead to the false positive check in linkUtils.getLink.
         * We need to treat absolute link to home as home link and not external.
         */
        const linkProps = linkUtils.getLinkProps(item.link);
        delete linkProps.type;
        // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
        // @ts-ignore TODO: Fix type for getLink after types merge in TB
        result.link = linkProps;
      }

      if (item.isCurrent) {
        result.isCurrent = item.isCurrent;
      }

      return result;
    });
  };

  const initialBreadcrumbs = makeInitialBreadcrumbs(
    envData,
    platformUtils,
    props,
  );

  const [state, setState] = createSdkState({
    breadcrumbsMetaData: makeBreadcrumbsMeta(initialBreadcrumbs),
  });

  return {
    get items() {
      return state.breadcrumbsMetaData.map(breadcrumb => {
        if (breadcrumb.link) {
          return {
            ...breadcrumb,
            // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
            // @ts-ignore TODO: Fix type for getLink after types merge in TB
            link: linkUtils.getLink(breadcrumb.link),
          };
        }

        return breadcrumb;
      }) as Array<Breadcrumb>;
    },

    set items(value) {
      const cleanedValue = makeBreadcrumbsMeta(value);

      setState({ breadcrumbsMetaData: cleanedValue });
      /**
       * We should ignore user settings related to current and home page
       * received from editor panel
       */
      setProps(
        makeBreadcrumbsProps(cleanedValue).then(breadcrumbsProps => ({
          ...breadcrumbsProps,
          showHomePage: true,
          showCurrentPage: true,
        })),
      );
    },

    get isEllipsisVisible() {
      return (
        !props.shouldWrap &&
        canRenderEllipsis({
          ...props,
          breadcrumbs: state.breadcrumbsMetaData,
        })
      );
    },

    showEllipsis() {
      if (
        !canRenderEllipsis({
          ...props,
          breadcrumbs: state.breadcrumbsMetaData,
        })
      ) {
        reportWarning(ELLIPSIS_WARNING_MESSAGE);
      }

      setProps({ shouldWrap: false });
    },

    hideEllipsis() {
      setProps({ shouldWrap: true });
    },

    get itemsBeforeEllipsis() {
      return props.itemsBeforeCollapse;
    },

    set itemsBeforeEllipsis(value: number) {
      if (
        validateLimits(
          value,
          state.breadcrumbsMetaData,
          'Breadcrumbs.itemsBeforeEllipsis',
          'itemsBeforeEllipsis',
        )
      ) {
        setProps({ itemsBeforeCollapse: value });
      }
    },

    get itemsAfterEllipsis() {
      return props.itemsAfterCollapse;
    },

    set itemsAfterEllipsis(value: number) {
      if (
        validateLimits(
          value,
          state.breadcrumbsMetaData,
          'Breadcrumbs.itemsAfterEllipsis',
          'itemsAfterEllipsis',
        )
      ) {
        setProps({ itemsAfterCollapse: value });
      }
    },

    toJSON() {
      return {
        ...toJSONBase(metaData),
        items: resolveLinks(state.breadcrumbsMetaData),
        itemsBeforeEllipsis: props.itemsBeforeCollapse,
        itemsAfterEllipsis: props.itemsAfterCollapse,
        isEllipsisVisible: props.shouldWrap,
      };
    },
  };
};

const breadcrumbs = withValidation(
  breadcrumbsSDKFactory,
  validationSchema,
  customValidators,
);

const accessibilityPropsSDKFactory: IAccessibilitySDKFactory =
  createAccessibilityPropSDKFactory({
    enableLang: true,
    enableAriaLabel: true,
    enableAriaDescribedBy: false,
    enableAriaLabelledBy: false,
  });

export const sdk = composeSDKFactories<BreadcrumbsProps, IBreadcrumbsSDK>([
  createElementPropsSDKFactory(),
  breadcrumbs,
  accessibilityPropsSDKFactory,
]);

export default createComponentSDKModel(sdk);
